import { Upload, X } from 'lucide-react';
import { useId, useRef } from 'react';

import { cn } from '../lib/cn';
import { Button } from './Button';
import { Label } from './Label';

interface FilePickerBaseProps {
  label: string;
  accept?: string | undefined;
  buttonLabel?: string | undefined;
  helperText?: string | undefined;
  helperTextClassName?: string | undefined;
  helperTextPlacement?: 'inline' | 'below' | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
  buttonClassName?: string | undefined;
  showSelectedFileName?: boolean | undefined;
  clearable?: boolean | undefined;
}

export type FilePickerProps = FilePickerBaseProps &
  (
    | {
        multiple?: false | undefined;
        value: File | null;
        onFileChange: (file: File | null) => void;
        onFilesChange?: never;
      }
    | {
        multiple: true;
        value: File[];
        onFilesChange: (files: File[]) => void;
        onFileChange?: never;
      }
  );

export function FilePicker(props: FilePickerProps) {
  const {
    label,
    accept,
    buttonLabel = 'Choose file',
    helperText,
    helperTextClassName,
    helperTextPlacement = 'inline',
    error,
    required = false,
    disabled = false,
    className,
    buttonClassName,
    showSelectedFileName = true,
    clearable = true,
  } = props;
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleClear() {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (props.multiple) props.onFilesChange([]);
    else props.onFileChange(null);
  }

  const hasValue = props.multiple ? props.value.length > 0 : props.value !== null;
  const helper = resolveFilePickerHelper(props, helperText, showSelectedFileName);
  const helperElement = helper ? (
    <span className={cn('text-muted-foreground text-sm', helperTextClassName)}>{helper}</span>
  ) : null;

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={inputId}>
        {label}
        {required ? (
          <span className="text-destructive" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
        {required ? <span className="sr-only"> (required)</span> : null}
      </Label>
      <div className="flex flex-wrap items-center gap-3">
        {/* `relative` keeps the sr-only input inside the button. Without it the input is laid out
            against the nearest positioned ancestor (the fixed, overflow-hidden app shell), and
            focusing it after the file dialog scrolls that shell, leaving the screen blank. */}
        <label
          className={cn(
            'border-input bg-background hover:bg-muted relative inline-flex h-9 items-center gap-2 rounded-md border px-4 text-sm font-medium transition-colors',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            buttonClassName,
          )}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {buttonLabel}
          <input
            id={inputId}
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={props.multiple}
            required={required}
            aria-required={required}
            disabled={disabled}
            className="sr-only"
            onChange={(event) => {
              if (props.multiple) props.onFilesChange(Array.from(event.target.files ?? []));
              else props.onFileChange(event.target.files?.[0] ?? null);
            }}
          />
        </label>
        {hasValue || helperTextPlacement === 'inline' ? helperElement : null}
        {hasValue && clearable ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            onClick={handleClear}
          >
            <X className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Remove file</span>
          </Button>
        ) : null}
      </div>
      {!hasValue && helperTextPlacement === 'below' ? helperElement : null}
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </div>
  );
}

function resolveFilePickerHelper(
  props: FilePickerProps,
  helperText: string | undefined,
  showSelectedFileName: boolean,
) {
  if (!props.multiple) {
    return props.value && showSelectedFileName ? props.value.name : helperText;
  }
  if (!props.value.length || !showSelectedFileName) return helperText;
  return props.value.map((file) => file.name).join(', ');
}
